import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RetryIcon,
  Cancel as CancelIcon,
  NavigateNext as NavigateNextIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  HourglassEmpty as PendingIcon,
  RadioButtonChecked as RunningIcon
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent
} from '@mui/lab';
import automationService from '../../services/automationService';

const ExecutionDetail = () => {
  const { id, executionId } = useParams();
  const navigate = useNavigate();
  const projectId = id;
  
  const [loading, setLoading] = useState(true);
  const [execution, setExecution] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadExecutionData();
    
    // Poll for updates if execution is running
    const interval = setInterval(() => {
      if (execution && ['queued', 'running'].includes(execution.status)) {
        loadExecutionData();
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [executionId]);
  
  const loadExecutionData = async () => {
    try {
      const response = await automationService.getExecution(executionId);
      setExecution(response.data?.execution || response.execution);
    } catch (error) {
      console.error('Error loading execution:', error);
      setError('Failed to load execution details');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRetry = async () => {
    try {
      await automationService.retryExecution(executionId);
      await loadExecutionData();
    } catch (error) {
      console.error('Error retrying execution:', error);
    }
  };
  
  const handleCancel = async () => {
    try {
      await automationService.cancelExecution(executionId);
      await loadExecutionData();
    } catch (error) {
      console.error('Error cancelling execution:', error);
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'success':
        return <SuccessIcon color="success" />;
      case 'failed':
      case 'error':
        return <ErrorIcon color="error" />;
      case 'cancelled':
        return <CancelIcon color="disabled" />;
      case 'running':
        return <RunningIcon color="primary" />;
      case 'queued':
        return <PendingIcon color="action" />;
      default:
        return <WarningIcon color="warning" />;
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'success':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'cancelled':
        return 'default';
      case 'running':
        return 'primary';
      case 'queued':
        return 'info';
      default:
        return 'warning';
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  if (!execution) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Alert severity="warning">Execution not found</Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
        <Link 
          component="button"
          variant="body1"
          onClick={() => navigate(`/projects/${projectId}`)}
          underline="hover"
        >
          Project
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/projects/${projectId}/automation`)}
          underline="hover"
        >
          Automation
        </Link>
        <Typography color="text.primary">Execution</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              Execution Details
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {getStatusIcon(execution.status)}
              <Chip 
                label={execution.status} 
                color={getStatusColor(execution.status)}
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {new Date(execution.createdAt).toLocaleString()}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            {execution.status === 'failed' && (
              <Button
                variant="contained"
                startIcon={<RetryIcon />}
                onClick={handleRetry}
              >
                Retry
              </Button>
            )}
            {['queued', 'running'].includes(execution.status) && (
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
      
      <Grid container spacing={3}>
        {/* Execution Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Execution Info
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Workflow"
                  secondary={execution.workflowId?.name || execution.workflowId}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Trigger"
                  secondary={execution.trigger?.type}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Started At"
                  secondary={execution.startedAt ? new Date(execution.startedAt).toLocaleString() : 'Not started'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Completed At"
                  secondary={execution.completedAt ? new Date(execution.completedAt).toLocaleString() : 'Not completed'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Duration"
                  secondary={execution.duration ? `${execution.duration}ms` : 'N/A'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Execution Mode"
                  secondary={execution.executionMode}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
        
        {/* Node Executions */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Node Executions
            </Typography>
            {execution.nodeExecutions && execution.nodeExecutions.length > 0 ? (
              <Timeline>
                {execution.nodeExecutions.map((node, index) => (
                  <TimelineItem key={node.nodeId}>
                    <TimelineOppositeContent color="text.secondary">
                      {new Date(node.startedAt).toLocaleTimeString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color={getStatusColor(node.status)}>
                        {getStatusIcon(node.status)}
                      </TimelineDot>
                      {index < execution.nodeExecutions.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="h6" component="span">
                        {node.nodeType}
                      </Typography>
                      <Typography>{node.nodeId}</Typography>
                      {node.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {node.error.message}
                        </Alert>
                      )}
                      {node.duration && (
                        <Typography variant="caption" color="text.secondary">
                          Duration: {node.duration}ms
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            ) : (
              <Alert severity="info">No node executions recorded</Alert>
            )}
          </Paper>
        </Grid>
        
        {/* Error Details */}
        {execution.error && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="error">
                Error Details
              </Typography>
              <Alert severity="error">
                <Typography variant="subtitle2" gutterBottom>
                  {execution.error.message}
                </Typography>
                {execution.error.stack && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                      {execution.error.stack}
                    </Typography>
                  </Box>
                )}
              </Alert>
            </Paper>
          </Grid>
        )}
        
        {/* Result */}
        {execution.result && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Result
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {JSON.stringify(execution.result, null, 2)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default ExecutionDetail;